import React, { FC, Fragment, useEffect, useState } from 'react';

import { GridRowSelectionModel } from '@mui/x-data-grid';

import styles from './TimeseriesPage.module.scss';
import { useAppSelector } from 'store/storeHooks';
import { TimeseriesPageProps } from './TimeseriesPageProps';
import { SensorModel } from 'pages/TimeseriesPage/SensorModel';
import { TimeseriesService } from 'pages/TimeseriesPage/TimeseriesService';
import AssociatedSensors from 'pages/TimeseriesPage/AssociatedSensors/AssociatedSensors';
import AvailableSensors from 'pages/TimeseriesPage/AvailableSensors/AvailableSensors';
import { PowerplantState } from 'store/powerplant/powerplant-slice';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';


export const TimeseriesPage: FC<TimeseriesPageProps> = () => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [sensors, setSensors] = useState<SensorModel[]>([]);
  const [associatedSensors, setAssociatedSensors] = useState<SensorModel[]>([]);
  const [availableSensors, setAvailableSensors] = useState<SensorModel[]>([]);

  const powerplant: PowerplantState = useAppSelector(state => state.powerplant);

  useEffect(() => {
    getSensors();
  }, []);

  useEffect(() => {
    if (!sensors?.length) {
      return;
    }

    if (!powerplant.name) {
      setAssociatedSensors([]);
      setAvailableSensors([]);
      setTimeout(() => {
        const alertConfig: AlertConfigModel = {title: 'Action needed', message: 'Please select a powerplant!'};
        setCurrentAlert(alertConfig);
        setIsAlertDialogOpen(true);
      }, 500);
    } else {
      filterAssociatedSensors();
      filterAvailableSensors();
    }
  }, [sensors, powerplant]);

  const getSensors = async (): Promise<void> => {
    setIsFetching(true);
    try {
      const fetchedSensors = await TimeseriesService.getSensors();

      if (!fetchedSensors) {
        throw new Error('No sensors returned from service.');
      }

      setSensors(fetchedSensors);
    } catch (error) {
      setSensors([]);
      console.error('Failed at getting sensors.', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed at getting sensors. Please refresh, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    } finally {
      setIsFetching(false);
    }
  }

  const filterAssociatedSensors = (): void => {
    const associated = sensors.filter(sensor => {
      return sensor.associations === powerplant.name || sensor.associations?.includes(powerplant.name as string);
    });

    setAssociatedSensors(associated);
  }

  const filterAvailableSensors = (): void => {
    const available = sensors.filter(sensor => {
      return sensor.associations !== powerplant.name && !sensor.associations?.includes(powerplant.name as string);
    });

    setAvailableSensors(available);
  }

  const associateHandler = async (selectedSensors: GridRowSelectionModel): Promise<void> => {
    const body = {
      sensors: selectedSensors.map(sensor => {
        return {
          id: sensor as string,
          associate: true
        }
      })
    }

    await updateAssociations(body);
  }

  const unassociateHandler = async (selectedSensors: GridRowSelectionModel): Promise<void> => {
    const body = {
      sensors: selectedSensors.map(sensor => {
        return {
          id: sensor as string,
          associate: false
        }
      })
    }

    await updateAssociations(body);
  }

  const updateAssociations = async (body: any): Promise<void> => {
    try {
      const updateResult = await TimeseriesService.updateAssociations(powerplant._id!, body);

      if (!updateResult.ok) {
        throw new Error('Something went wrong trying to update the associations');
      }

      await getSensors();
    } catch (error) {
      console.error('Failed at updating associations.', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed at updating associations. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    }
  }

  return (
    <Fragment>
      <div
        className={styles.TimeseriesPage}
        data-testid="TimeseriesPage"
      >
        <AssociatedSensors associatedSensors={associatedSensors} isLoading={isFetching} onUnassociate={unassociateHandler} />
        <AvailableSensors availableSensors={availableSensors} isLoading={isFetching} onAssociate={associateHandler} />
      </div>

      { isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </Fragment>
  );
};
